import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { Button, Notice } from "../components/ui";
import { confirmationReconciliationKey, reconcileConfirmationTypes } from "./confirmation-reconciliation";

const typeOrder = ["Debitorenbestätigung", "Bankbestätigung", "Rechtsanwaltsbestätigung", "Andere Bestätigung"];

/** Reconciles the complete selection in one versioned, idempotent operation. */
export function ProjectConfirmations({ project, navigate, onReconciled }) {
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [version, setVersion] = useState(project.version);
  const [loadError, setLoadError] = useState(null);
  const [action, setAction] = useState({ status: "idle" });
  const retry = useRef(null);
  const selected = useMemo(() => new Set(items.map(item => item.bestaetigungsartId)), [items]);
  const names = useMemo(() => ({
    type: Object.fromEntries(types.map(item => [item.id, item.name])),
    status: Object.fromEntries(statuses.map(item => [item.id, item.name])),
  }), [types, statuses]);

  const load = async () => {
    try {
      const [confirmationData, typeData, statusData, mandate] = await Promise.all([
        api(`/confirmations?mandatId=${project.id}&limit=1000`),
        api("/reference-data/bestaetigungsarten?limit=100"),
        api("/reference-data/bestaetigungsstatus?limit=100"),
        api(`/mandates/${project.id}`),
      ]);
      setItems(confirmationData.items); setEnabled(confirmationData.items.length > 0); setTypes(typeData.items.filter(item => item.aktiv !== false).sort((a, b) => {
        const ai = typeOrder.indexOf(a.name), bi = typeOrder.indexOf(b.name);
        return (ai < 0 ? typeOrder.length : ai) - (bi < 0 ? typeOrder.length : bi);
      }));
      setStatuses(statusData.items); setVersion(mandate.version); setLoadError(null);
    } catch (error) { setLoadError(error); }
  };
  useEffect(() => { load(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reconcile = async (ids, idempotencyKey = confirmationReconciliationKey()) => {
    retry.current = { ids, idempotencyKey };
    setAction({ status: "saving" });
    try {
      const result = await reconcileConfirmationTypes({ mandateId: project.id, version, bestaetigungsartIds: ids, idempotencyKey });
      setItems(result.items); setEnabled(result.items.length > 0); setVersion(result.version); retry.current = null;
      setAction({ status: "saved" }); onReconciled?.(result);
    } catch (error) {
      setAction({ status: error.status === 403 ? "forbidden" : error.status === 409 ? "stale" : error.status === 400 ? "validation" : "network", error });
    }
  };
  const toggle = (typeId, checked) => reconcile(checked ? [...selected, typeId] : [...selected].filter(id => id !== typeId));
  const busy = action.status === "saving";
  return <section className="project-confirmations project-detail-section">
    <h3>Bestätigungen</h3>
    {loadError && <Notice tone="error">Bestätigungen konnten nicht geladen werden. <Button variant="secondary" onClick={load}>Erneut versuchen</Button></Notice>}
    {!loadError && <>
      <label><input type="checkbox" checked={enabled} disabled={busy || (!selected.size && !types.length)} onChange={event => event.target.checked ? setEnabled(true) : reconcile([])} /> Drittbestätigung benötigt</label>
      {enabled && <div className="confirmation-type-selection">{types.map(type => <label key={type.id}>
        <input type="checkbox" checked={selected.has(type.id)} disabled={busy} onChange={event => toggle(type.id, event.target.checked)} /> {type.name}
      </label>)}</div>}
      {action.status === "saving" && <Notice>Bestätigungen werden gespeichert …</Notice>}
      {action.status === "saved" && <Notice>Bestätigungen gespeichert.</Notice>}
      {["validation", "forbidden", "network", "stale"].includes(action.status) && <Notice tone="error"><strong>{action.status === "validation" ? "Auswahl ungültig." : action.status === "forbidden" ? "Keine Berechtigung." : action.status === "stale" ? "Veraltete Projektversion." : "Netzwerkfehler."}</strong> {action.error?.message} <Button variant="secondary" onClick={action.status === "stale" ? load : () => reconcile(retry.current.ids, retry.current.idempotencyKey)}>Erneut versuchen</Button></Notice>}
      {items.length > 0 && <ul>{items.map(item => <li key={item.id}><button type="button" className="confirmation-link" onClick={() => navigate(`/bestaetigungen?mandatId=${encodeURIComponent(project.id)}&bestaetigungId=${encodeURIComponent(item.id)}`)}>{names.type[item.bestaetigungsartId] || "Unbekannte Bestätigungsart"} · {names.status[item.bestaetigungsstatusId] || "Unbekannter Status"}</button></li>)}</ul>}
    </>}
    <Button variant="secondary" onClick={() => navigate(`/bestaetigungen?mandatId=${encodeURIComponent(project.id)}${items[0] ? `&bestaetigungId=${encodeURIComponent(items[0].id)}` : ""}`)}>Bestätigungen anzeigen</Button>
  </section>;
}
